import type { Locale } from '@/config/locales';

export interface PartnerVisibilityStrings {
  back: string;
  navigationTitle: string;
  pageHeading: string;
  fulfilledStoresOnlyExplanation: string;
  privacyNoticeLink: string;
  privacyNoticeVersionLabel: string;
  loading: string;
  emptyFulfilledStores: string;
  loginRequired: string;
  loginCta: string;
  unavailableStore: string;
  grant: string;
  withdraw: string;
  withdrawConfirmTitle: string;
  withdrawConfirmBody: string;
  withdrawConfirmCancel: string;
  withdrawConfirmAction: string;
  saving: string;
  retry: string;
  grantSuccess: string;
  withdrawSuccess: string;
  loadError: string;
  mutationError: string;
  loadMore: string;
  loadingMore: string;
  paginationError: string;
  expiredGrantExplanation: string;
  withdrawPreviousGrant: string;
  privacyRequestInProgressTitle: string;
  privacyRequestInProgressExplanation: string;
  storeConsentSwitchLabel: string;
  storeNameLabel: string;
  consentGrantedStatus: string;
  consentWithdrawnStatus: string;
  consentExpiredStatus: string;
  consentUnavailableStatus: string;
}

export const partnerVisibilityStringsByLocale: Record<Locale, PartnerVisibilityStrings> = {
  es: {
    back: 'Volver',
    navigationTitle: 'Privacidad por tienda',
    pageHeading: 'Configuración de privacidad por tienda',
    fulfilledStoresOnlyExplanation:
      'Solo aparecen las tiendas donde tienes al menos un pedido entregado o recogido.',
    privacyNoticeLink: 'Consultar el Aviso de Privacidad',
    privacyNoticeVersionLabel: 'Versión del aviso',
    loading: 'Cargando tus preferencias…',
    emptyFulfilledStores: 'Aún no tienes pedidos entregados o recogidos en ninguna tienda.',
    loginRequired: 'Inicia sesión para administrar estas preferencias.',
    loginCta: 'Iniciar sesión',
    unavailableStore:
      'Esta tienda ya no está disponible, pero conservamos tu preferencia anterior.',
    grant: 'Permitir',
    withdraw: 'Retirar consentimiento',
    withdrawConfirmTitle: '¿Retirar el consentimiento?',
    withdrawConfirmBody:
      'La tienda y los terceros autorizados dejarán de acceder a tus datos personales para las finalidades descritas en el Aviso de Privacidad. Puedes volver a otorgar el consentimiento después.',
    withdrawConfirmCancel: 'Conservar consentimiento',
    withdrawConfirmAction: 'Sí, retirar',
    saving: 'Guardando…',
    retry: 'Volver a intentar',
    grantSuccess: 'Consentimiento otorgado.',
    withdrawSuccess: 'Consentimiento retirado.',
    loadError: 'No pudimos cargar tus preferencias de privacidad.',
    mutationError: 'No pudimos guardar el cambio. Inténtalo de nuevo.',
    loadMore: 'Cargar más',
    loadingMore: 'Cargando más…',
    paginationError: 'No pudimos cargar más tiendas.',
    expiredGrantExplanation:
      'Tu consentimiento anterior quedó registrado con una versión previa del aviso y ya no está vigente. Puedes otorgarlo de nuevo.',
    withdrawPreviousGrant: 'Retirar consentimiento anterior',
    privacyRequestInProgressTitle: 'Solicitud ARCO de Cancelación en proceso',
    privacyRequestInProgressExplanation:
      'Mientras procesamos tu solicitud ARCO de Cancelación, no puedes otorgar un nuevo consentimiento, pero sí puedes retirar cualquier consentimiento vigente o anterior.',
    storeConsentSwitchLabel:
      'Acceso de la tienda y terceros autorizados a tus datos personales',
    storeNameLabel: 'Tienda',
    consentGrantedStatus: 'Consentimiento vigente',
    consentWithdrawnStatus: 'Consentimiento retirado',
    consentExpiredStatus: 'Consentimiento anterior no vigente',
    consentUnavailableStatus: 'Preferencia no disponible',
  },
  en: {
    back: 'Back',
    navigationTitle: 'Privacy by Store',
    pageHeading: 'Store privacy settings',
    fulfilledStoresOnlyExplanation:
      'Only Stores where you have at least one delivered or picked-up order are shown.',
    privacyNoticeLink: 'View Privacy Notice',
    privacyNoticeVersionLabel: 'Notice version',
    loading: 'Loading your preferences…',
    emptyFulfilledStores: 'You do not have a delivered or picked-up order from any Store yet.',
    loginRequired: 'Log in to manage these preferences.',
    loginCta: 'Log in',
    unavailableStore:
      'This Store is no longer available, but we keep your previous preference on record.',
    grant: 'Allow',
    withdraw: 'Withdraw consent',
    withdrawConfirmTitle: 'Withdraw consent?',
    withdrawConfirmBody:
      'This Store and authorized third parties will stop accessing your personal data for the purposes described in the Privacy Notice. You can grant consent again later.',
    withdrawConfirmCancel: 'Keep consent',
    withdrawConfirmAction: 'Yes, withdraw',
    saving: 'Saving…',
    retry: 'Try again',
    grantSuccess: 'Consent granted.',
    withdrawSuccess: 'Consent withdrawn.',
    loadError: 'We could not load your privacy preferences.',
    mutationError: 'We could not save the change. Try again.',
    loadMore: 'Load More',
    loadingMore: 'Loading more…',
    paginationError: 'We could not load more Stores.',
    expiredGrantExplanation:
      'Your previous consent was recorded under an older notice and is no longer current. You can grant it again.',
    withdrawPreviousGrant: 'Withdraw previous consent',
    privacyRequestInProgressTitle: 'ARCO data-deletion request in progress',
    privacyRequestInProgressExplanation:
      'You cannot grant new consent while your ARCO data-deletion request is being processed, but you can still withdraw any existing or previous consent.',
    storeConsentSwitchLabel:
      'Access by this Store and authorized third parties to your personal data',
    storeNameLabel: 'Store',
    consentGrantedStatus: 'Consent current',
    consentWithdrawnStatus: 'Consent withdrawn',
    consentExpiredStatus: 'Previous consent no longer current',
    consentUnavailableStatus: 'Preference unavailable',
  },
  ko: {
    back: '뒤로',
    navigationTitle: '스토어별 개인정보',
    pageHeading: '스토어별 개인정보 설정',
    fulfilledStoresOnlyExplanation:
      '배달 완료 또는 픽업 완료 주문이 한 건 이상 있는 스토어만 표시됩니다.',
    privacyNoticeLink: '개인정보 처리방침 보기',
    privacyNoticeVersionLabel: '처리방침 버전',
    loading: '개인정보 설정을 불러오는 중…',
    emptyFulfilledStores: '아직 배달 완료 또는 픽업 완료 주문이 있는 스토어가 없습니다.',
    loginRequired: '이 설정을 관리하려면 로그인하세요.',
    loginCta: '로그인',
    unavailableStore: '현재 이용할 수 없는 스토어지만 이전 설정은 기록으로 보관됩니다.',
    grant: '허용',
    withdraw: '동의 철회',
    withdrawConfirmTitle: '동의를 철회할까요?',
    withdrawConfirmBody:
      '이 스토어와 승인된 제3자는 개인정보 처리방침에 설명된 목적을 위해 고객님의 개인정보에 더 이상 접근하지 않습니다. 나중에 다시 동의할 수 있습니다.',
    withdrawConfirmCancel: '동의 유지',
    withdrawConfirmAction: '철회하기',
    saving: '저장 중…',
    retry: '다시 시도',
    grantSuccess: '동의했습니다.',
    withdrawSuccess: '동의를 철회했습니다.',
    loadError: '개인정보 설정을 불러오지 못했습니다.',
    mutationError: '변경 내용을 저장하지 못했습니다. 다시 시도해 주세요.',
    loadMore: '더 보기',
    loadingMore: '더 불러오는 중…',
    paginationError: '스토어를 더 불러오지 못했습니다.',
    expiredGrantExplanation:
      '이전 동의는 과거 버전의 처리방침에 기록되어 현재는 유효하지 않습니다. 다시 동의할 수 있습니다.',
    withdrawPreviousGrant: '이전 동의 철회',
    privacyRequestInProgressTitle: '개인정보 삭제 요청 처리 중',
    privacyRequestInProgressExplanation:
      '개인정보 삭제 요청을 처리하는 동안 새로운 동의는 할 수 없지만, 현재 또는 이전 동의는 계속 철회할 수 있습니다.',
    storeConsentSwitchLabel:
      '이 스토어 및 승인된 제3자의 고객님 개인정보 접근 허용',
    storeNameLabel: '스토어',
    consentGrantedStatus: '현재 유효한 동의',
    consentWithdrawnStatus: '철회된 동의',
    consentExpiredStatus: '유효하지 않은 이전 동의',
    consentUnavailableStatus: '설정할 수 없음',
  },
  pt: {
    back: 'Voltar',
    navigationTitle: 'Privacidade por loja',
    pageHeading: 'Configurações de privacidade por loja',
    fulfilledStoresOnlyExplanation:
      'São exibidas apenas as lojas em que você tem pelo menos um pedido entregue ou retirado.',
    privacyNoticeLink: 'Ver Aviso de Privacidade',
    privacyNoticeVersionLabel: 'Versão do aviso',
    loading: 'Carregando suas preferências…',
    emptyFulfilledStores: 'Você ainda não tem pedidos entregues ou retirados em nenhuma loja.',
    loginRequired: 'Entre para gerenciar estas preferências.',
    loginCta: 'Entrar',
    unavailableStore:
      'Esta loja não está mais disponível, mas mantemos sua preferência anterior registrada.',
    grant: 'Permitir',
    withdraw: 'Retirar consentimento',
    withdrawConfirmTitle: 'Retirar o consentimento?',
    withdrawConfirmBody:
      'Esta loja e terceiros autorizados deixarão de acessar seus dados pessoais para as finalidades descritas no Aviso de Privacidade. Você poderá conceder o consentimento novamente depois.',
    withdrawConfirmCancel: 'Manter consentimento',
    withdrawConfirmAction: 'Sim, retirar',
    saving: 'Salvando…',
    retry: 'Tentar novamente',
    grantSuccess: 'Consentimento concedido.',
    withdrawSuccess: 'Consentimento retirado.',
    loadError: 'Não foi possível carregar suas preferências de privacidade.',
    mutationError: 'Não foi possível salvar a alteração. Tente novamente.',
    loadMore: 'Carregar mais',
    loadingMore: 'Carregando mais…',
    paginationError: 'Não foi possível carregar mais lojas.',
    expiredGrantExplanation:
      'Seu consentimento anterior foi registrado com uma versão antiga do aviso e não está mais vigente. Você pode concedê-lo novamente.',
    withdrawPreviousGrant: 'Retirar consentimento anterior',
    privacyRequestInProgressTitle: 'Solicitação ARCO de exclusão de dados em andamento',
    privacyRequestInProgressExplanation:
      'Você não pode conceder um novo consentimento enquanto sua solicitação ARCO de exclusão de dados estiver sendo processada, mas ainda pode retirar qualquer consentimento atual ou anterior.',
    storeConsentSwitchLabel:
      'Acesso desta loja e de terceiros autorizados aos seus dados pessoais',
    storeNameLabel: 'Loja',
    consentGrantedStatus: 'Consentimento vigente',
    consentWithdrawnStatus: 'Consentimento retirado',
    consentExpiredStatus: 'Consentimento anterior não vigente',
    consentUnavailableStatus: 'Preferência indisponível',
  },
  fr: {
    back: 'Retour',
    navigationTitle: 'Confidentialité par établissement',
    pageHeading: 'Paramètres de confidentialité par établissement',
    fulfilledStoresOnlyExplanation:
      'Seuls les établissements où vous avez au moins une commande livrée ou retirée sont affichés.',
    privacyNoticeLink: 'Consulter l’avis de confidentialité',
    privacyNoticeVersionLabel: 'Version de l’avis',
    loading: 'Chargement de vos préférences…',
    emptyFulfilledStores:
      'Vous n’avez encore aucune commande livrée ou retirée dans un établissement.',
    loginRequired: 'Connectez-vous pour gérer ces préférences.',
    loginCta: 'Se connecter',
    unavailableStore:
      'Cet établissement n’est plus disponible, mais votre préférence précédente reste enregistrée.',
    grant: 'Autoriser',
    withdraw: 'Retirer le consentement',
    withdrawConfirmTitle: 'Retirer le consentement ?',
    withdrawConfirmBody:
      'Cet établissement et les tiers autorisés cesseront d’accéder à vos données personnelles aux fins décrites dans l’avis de confidentialité. Vous pourrez accorder de nouveau votre consentement plus tard.',
    withdrawConfirmCancel: 'Conserver le consentement',
    withdrawConfirmAction: 'Oui, retirer',
    saving: 'Enregistrement…',
    retry: 'Réessayer',
    grantSuccess: 'Consentement accordé.',
    withdrawSuccess: 'Consentement retiré.',
    loadError: 'Impossible de charger vos préférences de confidentialité.',
    mutationError: 'Impossible d’enregistrer la modification. Réessayez.',
    loadMore: 'Afficher plus',
    loadingMore: 'Chargement…',
    paginationError: 'Impossible de charger d’autres établissements.',
    expiredGrantExplanation:
      'Votre ancien consentement a été enregistré avec une version antérieure de l’avis et n’est plus valable. Vous pouvez le renouveler.',
    withdrawPreviousGrant: 'Retirer l’ancien consentement',
    privacyRequestInProgressTitle: 'Demande d’effacement en cours',
    privacyRequestInProgressExplanation:
      'Vous ne pouvez pas accorder de nouveau consentement pendant le traitement de votre demande d’effacement, mais vous pouvez toujours retirer tout consentement actuel ou antérieur.',
    storeConsentSwitchLabel:
      'Accès de cet établissement et des tiers autorisés à vos données personnelles',
    storeNameLabel: 'Établissement',
    consentGrantedStatus: 'Consentement en vigueur',
    consentWithdrawnStatus: 'Consentement retiré',
    consentExpiredStatus: 'Ancien consentement non valable',
    consentUnavailableStatus: 'Préférence indisponible',
  },
  zh: {
    back: '返回',
    navigationTitle: '门店隐私',
    pageHeading: '各门店隐私设置',
    fulfilledStoresOnlyExplanation: '仅显示您至少有一笔已送达或已取餐订单的门店。',
    privacyNoticeLink: '查看隐私声明',
    privacyNoticeVersionLabel: '声明版本',
    loading: '正在加载您的偏好设置…',
    emptyFulfilledStores: '您还没有任何门店的已送达或已取餐订单。',
    loginRequired: '请登录以管理这些偏好设置。',
    loginCta: '登录',
    unavailableStore: '此门店已不可用，但我们仍保留您之前的偏好记录。',
    grant: '允许',
    withdraw: '撤回同意',
    withdrawConfirmTitle: '要撤回同意吗？',
    withdrawConfirmBody:
      '本门店及获授权的第三方将不再出于《隐私声明》中所述的目的访问您的个人数据。您之后可以再次授予同意。',
    withdrawConfirmCancel: '保留同意',
    withdrawConfirmAction: '确认撤回',
    saving: '正在保存…',
    retry: '重试',
    grantSuccess: '已授予同意。',
    withdrawSuccess: '已撤回同意。',
    loadError: '无法加载您的隐私偏好设置。',
    mutationError: '无法保存更改，请重试。',
    loadMore: '加载更多',
    loadingMore: '正在加载更多…',
    paginationError: '无法加载更多门店。',
    expiredGrantExplanation:
      '您之前的同意是依据旧版隐私声明记录的，现已不再有效。您可以重新授予同意。',
    withdrawPreviousGrant: '撤回之前的同意',
    privacyRequestInProgressTitle: '删除请求处理中',
    privacyRequestInProgressExplanation:
      '删除请求处理期间，您无法授予新的同意，但仍可撤回任何现有或之前的同意。',
    storeConsentSwitchLabel: '本门店及获授权的第三方访问您的个人数据',
    storeNameLabel: '门店',
    consentGrantedStatus: '同意有效',
    consentWithdrawnStatus: '同意已撤回',
    consentExpiredStatus: '之前的同意已失效',
    consentUnavailableStatus: '偏好设置不可用',
  },
  ja: {
    back: '戻る',
    navigationTitle: '店舗ごとのプライバシー',
    pageHeading: '店舗ごとのプライバシー設定',
    fulfilledStoresOnlyExplanation:
      '配達完了または受け取り完了の注文が1件以上ある店舗のみ表示されます。',
    privacyNoticeLink: 'プライバシー通知を見る',
    privacyNoticeVersionLabel: '通知のバージョン',
    loading: '設定を読み込んでいます…',
    emptyFulfilledStores: '配達完了または受け取り完了の注文がある店舗はまだありません。',
    loginRequired: 'この設定を管理するにはログインしてください。',
    loginCta: 'ログイン',
    unavailableStore: 'この店舗は現在利用できませんが、以前の設定は記録として保持されます。',
    grant: '許可する',
    withdraw: '同意を撤回',
    withdrawConfirmTitle: '同意を撤回しますか？',
    withdrawConfirmBody:
      'この店舗および許可された第三者は、プライバシー通知に記載された目的でお客様の個人データにアクセスしなくなります。後から再度同意できます。',
    withdrawConfirmCancel: '同意を維持',
    withdrawConfirmAction: '撤回する',
    saving: '保存しています…',
    retry: '再試行',
    grantSuccess: '同意しました。',
    withdrawSuccess: '同意を撤回しました。',
    loadError: 'プライバシー設定を読み込めませんでした。',
    mutationError: '変更を保存できませんでした。もう一度お試しください。',
    loadMore: 'さらに読み込む',
    loadingMore: 'さらに読み込んでいます…',
    paginationError: '店舗をさらに読み込めませんでした。',
    expiredGrantExplanation:
      '以前の同意は古いバージョンの通知に基づくため、現在は有効ではありません。改めて同意できます。',
    withdrawPreviousGrant: '以前の同意を撤回',
    privacyRequestInProgressTitle: '削除請求を処理中',
    privacyRequestInProgressExplanation:
      '削除請求の処理中は新たな同意はできませんが、現在または以前の同意は引き続き撤回できます。',
    storeConsentSwitchLabel:
      'この店舗および許可された第三者によるお客様の個人データへのアクセス',
    storeNameLabel: '店舗',
    consentGrantedStatus: '有効な同意',
    consentWithdrawnStatus: '撤回済み',
    consentExpiredStatus: '以前の同意は無効',
    consentUnavailableStatus: '設定できません',
  },
  hi: {
    back: 'वापस जाएँ',
    navigationTitle: 'स्टोर के अनुसार गोपनीयता',
    pageHeading: 'स्टोर के अनुसार गोपनीयता सेटिंग्स',
    fulfilledStoresOnlyExplanation:
      'केवल वे स्टोर दिखते हैं जहाँ आपका कम से कम एक ऑर्डर डिलीवर हुआ है या आपने स्टोर से पिक अप किया है।',
    privacyNoticeLink: 'गोपनीयता सूचना देखें',
    privacyNoticeVersionLabel: 'सूचना का संस्करण',
    loading: 'आपकी प्राथमिकताएँ लोड हो रही हैं…',
    emptyFulfilledStores:
      'अभी किसी स्टोर में आपका कोई ऑर्डर डिलीवर नहीं हुआ है और न ही आपने स्टोर से पिक अप किया है।',
    loginRequired: 'इन प्राथमिकताओं को प्रबंधित करने के लिए लॉग इन करें।',
    loginCta: 'लॉग इन',
    unavailableStore: 'यह स्टोर अब उपलब्ध नहीं है, लेकिन आपकी पिछली प्राथमिकता दर्ज है।',
    grant: 'अनुमति दें',
    withdraw: 'सहमति वापस लें',
    withdrawConfirmTitle: 'सहमति वापस लें?',
    withdrawConfirmBody:
      'यह स्टोर और अधिकृत तृतीय पक्ष गोपनीयता सूचना में बताए गए उद्देश्यों के लिए आपके व्यक्तिगत डेटा तक पहुँच बंद कर देंगे। आप बाद में फिर से सहमति दे सकते हैं।',
    withdrawConfirmCancel: 'सहमति बनाए रखें',
    withdrawConfirmAction: 'हाँ, वापस लें',
    saving: 'सहेजा जा रहा है…',
    retry: 'फिर कोशिश करें',
    grantSuccess: 'सहमति दे दी गई है।',
    withdrawSuccess: 'सहमति वापस ले ली गई है।',
    loadError: 'आपकी गोपनीयता प्राथमिकताएँ लोड नहीं हो सकीं।',
    mutationError: 'बदलाव सहेजा नहीं जा सका। फिर कोशिश करें।',
    loadMore: 'और लोड करें',
    loadingMore: 'और लोड हो रहा है…',
    paginationError: 'और स्टोर लोड नहीं हो सके।',
    expiredGrantExplanation:
      'आपकी पिछली सहमति सूचना के पुराने संस्करण पर दर्ज थी और अब मान्य नहीं है। आप फिर से सहमति दे सकते हैं।',
    withdrawPreviousGrant: 'पिछली सहमति वापस लें',
    privacyRequestInProgressTitle: 'हटाने का अनुरोध प्रक्रियाधीन है',
    privacyRequestInProgressExplanation:
      'हटाने के अनुरोध की प्रक्रिया के दौरान आप नई सहमति नहीं दे सकते, लेकिन मौजूदा या पिछली सहमति वापस ले सकते हैं।',
    storeConsentSwitchLabel:
      'इस स्टोर और अधिकृत तृतीय पक्षों की आपके व्यक्तिगत डेटा तक पहुँच',
    storeNameLabel: 'स्टोर',
    consentGrantedStatus: 'सहमति मान्य है',
    consentWithdrawnStatus: 'सहमति वापस ली गई',
    consentExpiredStatus: 'पिछली सहमति अब मान्य नहीं',
    consentUnavailableStatus: 'प्राथमिकता उपलब्ध नहीं',
  },
  de: {
    back: 'Zurück',
    navigationTitle: 'Datenschutz pro Geschäft',
    pageHeading: 'Datenschutzeinstellungen pro Geschäft',
    fulfilledStoresOnlyExplanation:
      'Es werden nur Geschäfte angezeigt, bei denen mindestens eine Bestellung geliefert oder abgeholt wurde.',
    privacyNoticeLink: 'Datenschutzhinweis ansehen',
    privacyNoticeVersionLabel: 'Version des Hinweises',
    loading: 'Ihre Einstellungen werden geladen…',
    emptyFulfilledStores:
      'Sie haben noch keine gelieferte oder abgeholte Bestellung bei einem Geschäft.',
    loginRequired: 'Melden Sie sich an, um diese Einstellungen zu verwalten.',
    loginCta: 'Anmelden',
    unavailableStore:
      'Dieses Geschäft ist nicht mehr verfügbar, Ihre frühere Einstellung bleibt jedoch gespeichert.',
    grant: 'Erlauben',
    withdraw: 'Einwilligung widerrufen',
    withdrawConfirmTitle: 'Einwilligung widerrufen?',
    withdrawConfirmBody:
      'Dieses Geschäft und autorisierte Dritte greifen für die im Datenschutzhinweis beschriebenen Zwecke nicht mehr auf Ihre personenbezogenen Daten zu. Sie können später erneut einwilligen.',
    withdrawConfirmCancel: 'Einwilligung beibehalten',
    withdrawConfirmAction: 'Ja, widerrufen',
    saving: 'Wird gespeichert…',
    retry: 'Erneut versuchen',
    grantSuccess: 'Einwilligung erteilt.',
    withdrawSuccess: 'Einwilligung widerrufen.',
    loadError: 'Ihre Datenschutzeinstellungen konnten nicht geladen werden.',
    mutationError: 'Die Änderung konnte nicht gespeichert werden. Versuchen Sie es erneut.',
    loadMore: 'Mehr laden',
    loadingMore: 'Weitere werden geladen…',
    paginationError: 'Weitere Geschäfte konnten nicht geladen werden.',
    expiredGrantExplanation:
      'Ihre frühere Einwilligung wurde zu einer älteren Fassung des Hinweises erfasst und ist nicht mehr aktuell. Sie können erneut einwilligen.',
    withdrawPreviousGrant: 'Frühere Einwilligung widerrufen',
    privacyRequestInProgressTitle: 'Löschantrag wird bearbeitet',
    privacyRequestInProgressExplanation:
      'Während Ihr Löschantrag bearbeitet wird, können Sie keine neue Einwilligung erteilen, aber weiterhin jede bestehende oder frühere Einwilligung widerrufen.',
    storeConsentSwitchLabel:
      'Zugriff dieses Geschäfts und autorisierter Dritter auf Ihre personenbezogenen Daten',
    storeNameLabel: 'Geschäft',
    consentGrantedStatus: 'Einwilligung aktuell',
    consentWithdrawnStatus: 'Einwilligung widerrufen',
    consentExpiredStatus: 'Frühere Einwilligung nicht mehr aktuell',
    consentUnavailableStatus: 'Einstellung nicht verfügbar',
  },
  it: {
    back: 'Indietro',
    navigationTitle: 'Privacy per punto vendita',
    pageHeading: 'Impostazioni privacy per punto vendita',
    fulfilledStoresOnlyExplanation:
      'Sono mostrati solo i punti vendita in cui hai almeno un ordine consegnato o ritirato.',
    privacyNoticeLink: 'Consulta l’informativa sulla privacy',
    privacyNoticeVersionLabel: 'Versione dell’informativa',
    loading: 'Caricamento delle preferenze…',
    emptyFulfilledStores:
      'Non hai ancora ordini consegnati o ritirati presso alcun punto vendita.',
    loginRequired: 'Accedi per gestire queste preferenze.',
    loginCta: 'Accedi',
    unavailableStore:
      'Questo punto vendita non è più disponibile, ma conserviamo la preferenza precedente.',
    grant: 'Consenti',
    withdraw: 'Revoca il consenso',
    withdrawConfirmTitle: 'Revocare il consenso?',
    withdrawConfirmBody:
      'Questo punto vendita e le terze parti autorizzate smetteranno di accedere ai tuoi dati personali per le finalità descritte nell’informativa sulla privacy. Potrai dare nuovamente il consenso in seguito.',
    withdrawConfirmCancel: 'Mantieni il consenso',
    withdrawConfirmAction: 'Sì, revoca',
    saving: 'Salvataggio…',
    retry: 'Riprova',
    grantSuccess: 'Consenso accordato.',
    withdrawSuccess: 'Consenso revocato.',
    loadError: 'Impossibile caricare le preferenze sulla privacy.',
    mutationError: 'Impossibile salvare la modifica. Riprova.',
    loadMore: 'Carica altro',
    loadingMore: 'Caricamento…',
    paginationError: 'Impossibile caricare altri punti vendita.',
    expiredGrantExplanation:
      'Il consenso precedente è stato registrato con una versione meno recente dell’informativa e non è più valido. Puoi accordarlo di nuovo.',
    withdrawPreviousGrant: 'Revoca il consenso precedente',
    privacyRequestInProgressTitle: 'Richiesta di cancellazione in corso',
    privacyRequestInProgressExplanation:
      'Mentre elaboriamo la richiesta di cancellazione, non puoi accordare un nuovo consenso, ma puoi comunque revocare qualsiasi consenso attuale o precedente.',
    storeConsentSwitchLabel:
      'Accesso di questo punto vendita e di terze parti autorizzate ai tuoi dati personali',
    storeNameLabel: 'Punto vendita',
    consentGrantedStatus: 'Consenso valido',
    consentWithdrawnStatus: 'Consenso revocato',
    consentExpiredStatus: 'Consenso precedente non più valido',
    consentUnavailableStatus: 'Preferenza non disponibile',
  },
  ru: {
    back: 'Назад',
    navigationTitle: 'Конфиденциальность по заведениям',
    pageHeading: 'Настройки конфиденциальности для каждого заведения',
    fulfilledStoresOnlyExplanation:
      'Показаны только заведения, где у вас есть хотя бы один доставленный заказ или заказ, полученный самовывозом.',
    privacyNoticeLink: 'Открыть уведомление о конфиденциальности',
    privacyNoticeVersionLabel: 'Версия уведомления',
    loading: 'Загружаем ваши настройки…',
    emptyFulfilledStores:
      'У вас пока нет ни доставленных заказов, ни заказов, полученных самовывозом.',
    loginRequired: 'Войдите, чтобы управлять этими настройками.',
    loginCta: 'Войти',
    unavailableStore:
      'Это заведение больше недоступно, но предыдущая настройка сохранена в истории.',
    grant: 'Разрешить',
    withdraw: 'Отозвать согласие',
    withdrawConfirmTitle: 'Отозвать согласие?',
    withdrawConfirmBody:
      'Это заведение и уполномоченные третьи лица перестанут получать доступ к вашим персональным данным для целей, описанных в уведомлении о конфиденциальности. Позже вы сможете снова дать согласие.',
    withdrawConfirmCancel: 'Сохранить согласие',
    withdrawConfirmAction: 'Да, отозвать',
    saving: 'Сохраняем…',
    retry: 'Повторить',
    grantSuccess: 'Согласие предоставлено.',
    withdrawSuccess: 'Согласие отозвано.',
    loadError: 'Не удалось загрузить настройки конфиденциальности.',
    mutationError: 'Не удалось сохранить изменение. Повторите попытку.',
    loadMore: 'Загрузить ещё',
    loadingMore: 'Загружаем ещё…',
    paginationError: 'Не удалось загрузить другие заведения.',
    expiredGrantExplanation:
      'Предыдущее согласие было записано для старой версии уведомления и больше не действует. Вы можете дать его снова.',
    withdrawPreviousGrant: 'Отозвать предыдущее согласие',
    privacyRequestInProgressTitle: 'Запрос на удаление обрабатывается',
    privacyRequestInProgressExplanation:
      'Пока обрабатывается ваш запрос на удаление, вы не можете дать новое согласие, но можете отозвать любое действующее или предыдущее согласие.',
    storeConsentSwitchLabel:
      'Доступ этого заведения и уполномоченных третьих лиц к вашим персональным данным',
    storeNameLabel: 'Заведение',
    consentGrantedStatus: 'Согласие действует',
    consentWithdrawnStatus: 'Согласие отозвано',
    consentExpiredStatus: 'Предыдущее согласие больше не действует',
    consentUnavailableStatus: 'Настройка недоступна',
  },
  ar: {
    back: 'رجوع',
    navigationTitle: 'الخصوصية لكل متجر',
    pageHeading: 'إعدادات الخصوصية لكل متجر',
    fulfilledStoresOnlyExplanation:
      'لا تظهر إلا المتاجر التي لديك فيها طلب واحد على الأقل تم توصيله أو استلامه من المتجر.',
    privacyNoticeLink: 'عرض إشعار الخصوصية',
    privacyNoticeVersionLabel: 'إصدار الإشعار',
    loading: 'جارٍ تحميل تفضيلاتك…',
    emptyFulfilledStores: 'ليس لديك بعد أي طلب تم توصيله أو استلامه من المتجر.',
    loginRequired: 'سجّل الدخول لإدارة هذه التفضيلات.',
    loginCta: 'تسجيل الدخول',
    unavailableStore: 'لم يعد هذا المتجر متاحًا، لكننا نحتفظ بتفضيلك السابق في السجل.',
    grant: 'السماح',
    withdraw: 'سحب الموافقة',
    withdrawConfirmTitle: 'هل تريد سحب الموافقة؟',
    withdrawConfirmBody:
      'سيتوقف هذا المتجر والجهات الخارجية المصرح لها عن الوصول إلى بياناتك الشخصية للأغراض الموضحة في إشعار الخصوصية. يمكنك منح الموافقة مرة أخرى لاحقًا.',
    withdrawConfirmCancel: 'الإبقاء على الموافقة',
    withdrawConfirmAction: 'نعم، اسحبها',
    saving: 'جارٍ الحفظ…',
    retry: 'إعادة المحاولة',
    grantSuccess: 'تم منح الموافقة.',
    withdrawSuccess: 'تم سحب الموافقة.',
    loadError: 'تعذر تحميل تفضيلات الخصوصية.',
    mutationError: 'تعذر حفظ التغيير. حاول مرة أخرى.',
    loadMore: 'تحميل المزيد',
    loadingMore: 'جارٍ تحميل المزيد…',
    paginationError: 'تعذر تحميل المزيد من المتاجر.',
    expiredGrantExplanation:
      'سُجلت موافقتك السابقة وفق إصدار أقدم من الإشعار ولم تعد سارية. يمكنك منحها من جديد.',
    withdrawPreviousGrant: 'سحب الموافقة السابقة',
    privacyRequestInProgressTitle: 'طلب الحذف قيد المعالجة',
    privacyRequestInProgressExplanation:
      'لا يمكنك منح موافقة جديدة أثناء معالجة طلب الحذف، لكن يمكنك سحب أي موافقة حالية أو سابقة.',
    storeConsentSwitchLabel:
      'وصول هذا المتجر والجهات الخارجية المصرح لها إلى بياناتك الشخصية',
    storeNameLabel: 'المتجر',
    consentGrantedStatus: 'الموافقة سارية',
    consentWithdrawnStatus: 'تم سحب الموافقة',
    consentExpiredStatus: 'الموافقة السابقة لم تعد سارية',
    consentUnavailableStatus: 'التفضيل غير متاح',
  },
  vi: {
    back: 'Quay lại',
    navigationTitle: 'Quyền riêng tư theo cửa hàng',
    pageHeading: 'Cài đặt quyền riêng tư theo cửa hàng',
    fulfilledStoresOnlyExplanation:
      'Chỉ hiển thị cửa hàng mà bạn có ít nhất một đơn đã giao tận nơi hoặc đã lấy tại cửa hàng.',
    privacyNoticeLink: 'Xem Thông báo quyền riêng tư',
    privacyNoticeVersionLabel: 'Phiên bản thông báo',
    loading: 'Đang tải tùy chọn của bạn…',
    emptyFulfilledStores:
      'Bạn chưa có đơn đã giao tận nơi hoặc đã lấy tại cửa hàng nào.',
    loginRequired: 'Đăng nhập để quản lý các tùy chọn này.',
    loginCta: 'Đăng nhập',
    unavailableStore:
      'Cửa hàng này không còn hoạt động, nhưng tùy chọn trước đây của bạn vẫn được lưu.',
    grant: 'Cho phép',
    withdraw: 'Rút lại sự đồng ý',
    withdrawConfirmTitle: 'Rút lại sự đồng ý?',
    withdrawConfirmBody:
      'Cửa hàng này và các bên thứ ba được ủy quyền sẽ ngừng truy cập dữ liệu cá nhân của bạn cho các mục đích được mô tả trong Thông báo quyền riêng tư. Bạn có thể đồng ý lại sau.',
    withdrawConfirmCancel: 'Giữ nguyên sự đồng ý',
    withdrawConfirmAction: 'Rút lại',
    saving: 'Đang lưu…',
    retry: 'Thử lại',
    grantSuccess: 'Đã đồng ý.',
    withdrawSuccess: 'Đã rút lại sự đồng ý.',
    loadError: 'Không thể tải tùy chọn quyền riêng tư của bạn.',
    mutationError: 'Không thể lưu thay đổi. Vui lòng thử lại.',
    loadMore: 'Tải thêm',
    loadingMore: 'Đang tải thêm…',
    paginationError: 'Không thể tải thêm cửa hàng.',
    expiredGrantExplanation:
      'Sự đồng ý trước đây của bạn được ghi nhận theo phiên bản thông báo cũ và không còn hiệu lực. Bạn có thể đồng ý lại.',
    withdrawPreviousGrant: 'Rút lại sự đồng ý trước đây',
    privacyRequestInProgressTitle: 'Đang xử lý yêu cầu xóa dữ liệu',
    privacyRequestInProgressExplanation:
      'Bạn không thể cấp sự đồng ý mới trong khi yêu cầu xóa dữ liệu đang được xử lý, nhưng vẫn có thể rút lại mọi sự đồng ý hiện tại hoặc trước đây.',
    storeConsentSwitchLabel:
      'Việc cửa hàng này và các bên thứ ba được ủy quyền truy cập dữ liệu cá nhân của bạn',
    storeNameLabel: 'Cửa hàng',
    consentGrantedStatus: 'Sự đồng ý còn hiệu lực',
    consentWithdrawnStatus: 'Đã rút lại sự đồng ý',
    consentExpiredStatus: 'Sự đồng ý trước đây đã hết hiệu lực',
    consentUnavailableStatus: 'Tùy chọn không khả dụng',
  },
  th: {
    back: 'ย้อนกลับ',
    navigationTitle: 'ความเป็นส่วนตัวแยกตามร้าน',
    pageHeading: 'การตั้งค่าความเป็นส่วนตัวแยกตามร้าน',
    fulfilledStoresOnlyExplanation:
      'แสดงเฉพาะร้านที่คุณมีคำสั่งซื้อที่จัดส่งสำเร็จหรือรับสินค้าที่ร้านแล้วอย่างน้อยหนึ่งรายการ',
    privacyNoticeLink: 'ดูประกาศความเป็นส่วนตัว',
    privacyNoticeVersionLabel: 'เวอร์ชันประกาศ',
    loading: 'กำลังโหลดการตั้งค่าของคุณ…',
    emptyFulfilledStores:
      'คุณยังไม่มีคำสั่งซื้อที่จัดส่งสำเร็จหรือรับสินค้าที่ร้านแล้วจากร้านใด',
    loginRequired: 'เข้าสู่ระบบเพื่อจัดการการตั้งค่าเหล่านี้',
    loginCta: 'เข้าสู่ระบบ',
    unavailableStore: 'ร้านนี้ไม่เปิดให้บริการแล้ว แต่เรายังคงเก็บการตั้งค่าเดิมของคุณไว้',
    grant: 'อนุญาต',
    withdraw: 'ถอนความยินยอม',
    withdrawConfirmTitle: 'ถอนความยินยอมหรือไม่',
    withdrawConfirmBody:
      'ร้านนี้และบุคคลที่สามที่ได้รับอนุญาตจะหยุดเข้าถึงข้อมูลส่วนบุคคลของคุณเพื่อวัตถุประสงค์ที่อธิบายไว้ในประกาศความเป็นส่วนตัว คุณสามารถให้ความยินยอมอีกครั้งภายหลังได้',
    withdrawConfirmCancel: 'คงความยินยอมไว้',
    withdrawConfirmAction: 'ถอนความยินยอม',
    saving: 'กำลังบันทึก…',
    retry: 'ลองอีกครั้ง',
    grantSuccess: 'ให้ความยินยอมแล้ว',
    withdrawSuccess: 'ถอนความยินยอมแล้ว',
    loadError: 'ไม่สามารถโหลดการตั้งค่าความเป็นส่วนตัวของคุณได้',
    mutationError: 'ไม่สามารถบันทึกการเปลี่ยนแปลงได้ โปรดลองอีกครั้ง',
    loadMore: 'โหลดเพิ่มเติม',
    loadingMore: 'กำลังโหลดเพิ่มเติม…',
    paginationError: 'ไม่สามารถโหลดร้านเพิ่มเติมได้',
    expiredGrantExplanation:
      'ความยินยอมเดิมของคุณบันทึกตามประกาศเวอร์ชันเก่าและไม่มีผลแล้ว คุณสามารถให้ความยินยอมใหม่ได้',
    withdrawPreviousGrant: 'ถอนความยินยอมเดิม',
    privacyRequestInProgressTitle: 'กำลังดำเนินการคำขอลบข้อมูล',
    privacyRequestInProgressExplanation:
      'คุณไม่สามารถให้ความยินยอมใหม่ระหว่างที่กำลังดำเนินการคำขอลบข้อมูล แต่ยังสามารถถอนความยินยอมที่มีอยู่หรือที่เคยให้ไว้ได้',
    storeConsentSwitchLabel:
      'การเข้าถึงข้อมูลส่วนบุคคลของคุณโดยร้านนี้และบุคคลที่สามที่ได้รับอนุญาต',
    storeNameLabel: 'ร้าน',
    consentGrantedStatus: 'ความยินยอมมีผล',
    consentWithdrawnStatus: 'ถอนความยินยอมแล้ว',
    consentExpiredStatus: 'ความยินยอมเดิมไม่มีผลแล้ว',
    consentUnavailableStatus: 'ไม่สามารถตั้งค่าได้',
  },
  nl: {
    back: 'Terug',
    navigationTitle: 'Privacy per vestiging',
    pageHeading: 'Privacyinstellingen per vestiging',
    fulfilledStoresOnlyExplanation:
      'Alleen vestigingen waar je ten minste één bezorgde of afgehaalde bestelling hebt, worden getoond.',
    privacyNoticeLink: 'Privacyverklaring bekijken',
    privacyNoticeVersionLabel: 'Versie van de verklaring',
    loading: 'Je voorkeuren worden geladen…',
    emptyFulfilledStores:
      'Je hebt nog geen bezorgde of afgehaalde bestelling bij een vestiging.',
    loginRequired: 'Log in om deze voorkeuren te beheren.',
    loginCta: 'Inloggen',
    unavailableStore:
      'Deze vestiging is niet meer beschikbaar, maar je eerdere voorkeur blijft geregistreerd.',
    grant: 'Toestaan',
    withdraw: 'Toestemming intrekken',
    withdrawConfirmTitle: 'Toestemming intrekken?',
    withdrawConfirmBody:
      'Deze vestiging en geautoriseerde derden krijgen voor de in de privacyverklaring beschreven doeleinden geen toegang meer tot je persoonsgegevens. Je kunt later opnieuw toestemming geven.',
    withdrawConfirmCancel: 'Toestemming behouden',
    withdrawConfirmAction: 'Ja, intrekken',
    saving: 'Opslaan…',
    retry: 'Opnieuw proberen',
    grantSuccess: 'Toestemming gegeven.',
    withdrawSuccess: 'Toestemming ingetrokken.',
    loadError: 'Je privacyvoorkeuren konden niet worden geladen.',
    mutationError: 'De wijziging kon niet worden opgeslagen. Probeer het opnieuw.',
    loadMore: 'Meer laden',
    loadingMore: 'Meer laden…',
    paginationError: 'Meer vestigingen konden niet worden geladen.',
    expiredGrantExplanation:
      'Je eerdere toestemming is vastgelegd bij een oudere versie van de verklaring en is niet meer geldig. Je kunt opnieuw toestemming geven.',
    withdrawPreviousGrant: 'Eerdere toestemming intrekken',
    privacyRequestInProgressTitle: 'Verwijderingsverzoek wordt verwerkt',
    privacyRequestInProgressExplanation:
      'Terwijl je verwijderingsverzoek wordt verwerkt, kun je geen nieuwe toestemming geven, maar nog steeds elke bestaande of eerdere toestemming intrekken.',
    storeConsentSwitchLabel:
      'Toegang van deze vestiging en geautoriseerde derden tot je persoonsgegevens',
    storeNameLabel: 'Vestiging',
    consentGrantedStatus: 'Toestemming geldig',
    consentWithdrawnStatus: 'Toestemming ingetrokken',
    consentExpiredStatus: 'Eerdere toestemming niet meer geldig',
    consentUnavailableStatus: 'Voorkeur niet beschikbaar',
  },
};

export function getPartnerVisibilityStrings(locale: Locale): PartnerVisibilityStrings {
  return partnerVisibilityStringsByLocale[locale];
}
